import { TitleCasePipe } from '@angular/common';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CalendarEvent, CalendarEventAction } from 'angular-calendar';
import { FacadeService } from 'src/app/core/services/facade.service';
import { Vacation } from '../models/vacation';

import {
	ConstantsService,
	MISSION_TYPE_PRESCRIPTION,
	MISSION_TYPE_REUNION_TYPE,
	PARAM_LABEL_BENEFICIAIRE,
	PARAM_LABEL_INTERVENANT,
	PARAM_LABEL_INTERVENTION
} from 'src/app/core/services/constant/constants.service';
import { Employee } from '../models/employee/employee';
import { Mission } from '../models/mission/mission';
import { State } from '../enums/state.enum';
import { Route } from '../models/route';
import { DateUtils } from './date-utils';

@Injectable()
export class ConvertObjectToCalendarEvent {
	/** secondary is back color */
	colors: any = {
		red: { primary: '#ad2121', secondary: '#FAE3E3' },
		blue: { primary: '#1e90ff', secondary: '#D1E8FF' },
		yellow: { primary: '#e3bc08', secondary: '#F4FF3A' },
		green: { primary: '#0b730f', secondary: '#82ff87' },
		orange: { primary: '#FF6600', secondary: '#fc9c5b' },
		purple: { primary: '#C5B1CD', secondary: '#F5E1FD' },
		grey: { primary: '#9d9d9d', secondary: '#e6e6e6' },

		lightgrey: { secondary: '#c7c9d9', primary: '#262c5e' },
		white: { secondary: '#ffffff', primary: '#262c5e' },
		dark: { secondary: '#9196b3', primary: '#262c5e' }
	};

	colorsPastelBase: any = [
		{ secondary: '#f19eaf', primary: '#262c5e' }, //pink
		{ secondary: '#668bc7', primary: '#262c5e' }, //blue
		{ secondary: '#8dcdcb', primary: '#262c5e' }, //green
		{ secondary: '#ed7387', primary: '#262c5e' }, //red
		{ secondary: '#a3acd8', primary: '#262c5e' } //violet
	];
	colorTransparent: any = {
		secondary: 'rgba(200, 200, 200, .7)',
		primary: 'rgba(200, 200, 200, .7)'
	}; // blanc-grisé transparent

	// Intervenant externe : light grey
	colorExternalEmployee: any = { secondary: '#c7c9d9', primary: '#262c5e' };

	public colorsPastel: any = [
		{ secondary: '#FFB3BA', primary: '#262c5e' }, // rose pastel
		{ secondary: '#FFDFBA', primary: '#262c5e' }, // orange doux
		{ secondary: '#FFFFBA', primary: '#262c5e' }, // jaune pastel
		{ secondary: '#BAFFC9', primary: '#262c5e' }, // vert menthe
		{ secondary: '#BAE1FF', primary: '#262c5e' }, // bleu ciel

		{ secondary: '#C9C9FF', primary: '#262c5e' }, // lavande pastel
		{ secondary: '#F7C5CC', primary: '#262c5e' }, // rose poudré
		{ secondary: '#F9E2AE', primary: '#262c5e' }, // jaune doux
		{ secondary: '#B5EAD7', primary: '#262c5e' }, // vert d’eau clair
		{ secondary: '#D0E2F2', primary: '#262c5e' }, // bleu glacier

		{ secondary: '#E2F0CB', primary: '#262c5e' }, // vert très doux
		{ secondary: '#D9C8F2', primary: '#262c5e' }, // violet clair
		{ secondary: '#FFD1DC', primary: '#262c5e' }, // rose bébé
		{ secondary: '#C1D3FE', primary: '#262c5e' }, // bleu lavande
		{ secondary: '#B8E0D2', primary: '#262c5e' }, // turquoise pastel

		{ secondary: '#F1C0E8', primary: '#262c5e' }, // lilas rosé
		{ secondary: '#FFDAC1', primary: '#262c5e' }, // pêche pastel
		{ secondary: '#C5DEDD', primary: '#262c5e' }, // bleu-vert pâle
		{ secondary: '#F3B0C3', primary: '#262c5e' }, // framboise adouci
		{ secondary: '#F6DFEB', primary: '#262c5e' }, // rose blanc

		{ secondary: '#E5E0FF', primary: '#262c5e' }, // violet blanc pastel
		{ secondary: '#FFEFBA', primary: '#262c5e' }, // jaune crème
		{ secondary: '#C3F0CA', primary: '#262c5e' }, // vert pastel clair
		{ secondary: '#BFD8B8', primary: '#262c5e' }, // vert amande
		{ secondary: '#E3C9D9', primary: '#262c5e' }, // vieux rose pastel

		{ secondary: '#A9D1D7', primary: '#262c5e' }, // bleu givré
		{ secondary: '#D7D4F0', primary: '#262c5e' }, // violet nuage
		{ secondary: '#FFCCCC', primary: '#262c5e' }, // rose clair classique
		{ secondary: '#FFCCE5', primary: '#262c5e' }, // rose bonbon
		{ secondary: '#E6CCFF', primary: '#262c5e' }, // violet lavande clair

		{ secondary: '#CCE5FF', primary: '#262c5e' }, // bleu pastel
		{ secondary: '#CCFFFF', primary: '#262c5e' }, // bleu turquoise très pâle
		{ secondary: '#CCFFE5', primary: '#262c5e' }, // vert d’eau glacé
		{ secondary: '#E5FFCC', primary: '#262c5e' }, // vert anis très pâle
		{ secondary: '#FFFFCC', primary: '#262c5e' }, // jaune très pâle

		{ secondary: '#FFE5CC', primary: '#262c5e' }, // orange crème pastel
		{ secondary: '#FFCCE0', primary: '#262c5e' }, // rose pétale
		{ secondary: '#FFDDEE', primary: '#262c5e' }, // rose très clair
		{ secondary: '#EEDDFF', primary: '#262c5e' }, // violet lumineux

		{ secondary: '#DDEEFF', primary: '#262c5e' }, // bleu ciel givré
		{ secondary: '#DFFFFF', primary: '#262c5e' }, // turquoise pastel lumineux
		{ secondary: '#DDFFEE', primary: '#262c5e' }, // vert givré très clair
		{ secondary: '#EEFFDD', primary: '#262c5e' }, // vert pastel très doux
		{ secondary: '#FFFFDD', primary: '#262c5e' }, // jaune blanc

		{ secondary: '#FFEEDD', primary: '#262c5e' }, // orange blanc pastel
		{ secondary: '#FADADD', primary: '#262c5e' }, // rose peau
		{ secondary: '#E6E6FA', primary: '#262c5e' } // lavande très pâle (standard)
	];
	nbrColorsPastel = this.colorsPastel.length;

	public publicHolidays = {
		'2024-01-01': '1er janvier',
		'2024-04-01': 'Lundi de Pâques',
		'2024-05-01': '1er mai',
		'2024-05-08': '8 mai',
		'2024-05-09': 'Ascension',
		'2024-05-20': 'Lundi de Pentecôte',
		'2024-07-14': '14 juillet',
		'2024-08-15': 'Assomption',
		'2024-11-01': 'Toussaint',
		'2024-11-11': '11 novembre',
		'2024-12-25': 'Jour de Noël',
		'2023-01-01': '1er janvier',
		'2023-04-10': 'Lundi de Pâques',
		'2023-05-01': '1er mai',
		'2023-05-08': '8 mai',
		'2023-05-18': 'Ascension',
		'2023-05-29': 'Lundi de Pentecôte',
		'2023-07-14': '14 juillet',
		'2023-08-15': 'Assomption',
		'2023-11-01': 'Toussaint',
		'2023-11-11': '11 novembre',
		'2023-12-25': 'Jour de Noël',
		'2022-01-01': '1er janvier',
		'2022-04-18': 'Lundi de Pâques',
		'2022-05-01': '1er mai',
		'2022-05-08': '8 mai',
		'2022-05-26': 'Ascension',
		'2022-06-06': 'Lundi de Pentecôte',
		'2022-07-14': '14 juillet',
		'2022-08-15': 'Assomption',
		'2022-11-01': 'Toussaint',
		'2022-11-11': '11 novembre',
		'2022-12-25': 'Jour de Noël'
	};

	actions: CalendarEventAction[] = [];

	isVehiculeActive: boolean;
	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isSAE = false;
	isMissionPlacesActive: boolean;

	state = State;

	labelBeneficiaireCapitalized: string;
	labelIntervenantCapitalized: string;
	labelHostCapitalized: string = 'Organisé par : ';
	labelInterventionCapitalized: string;

	breakLaunchDuration = 30; // minutes

	constructor(
		private titleCasePipe: TitleCasePipe,
		private sanitizer: DomSanitizer,
		private facade: FacadeService,
		private constantService: ConstantsService,
		private dateUtils: DateUtils
	) {
		this.labelBeneficiaireCapitalized =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_BENEFICIAIRE
			);
		this.labelIntervenantCapitalized =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_INTERVENANT
			);
		this.labelInterventionCapitalized =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_INTERVENTION
			);
		this.isVehiculeActive = this.facade.dataStorageService.isVehiculeActive();
		this.isSESSAD = this.facade.dataStorageService.isSESSAD();
		this.isSAAD = this.facade.dataStorageService.isSAAD();
		this.isSSIAD = this.facade.dataStorageService.isSSIAD();
		this.isSAE = this.facade.dataStorageService.isSAE();
		this.isMissionPlacesActive = this.isSESSAD;
	}

	getColorFromSpecialite(specName: string) {
		switch (specName) {
			case 'Electricité':
				return this.colors.red;
			case 'Mécanique':
				return this.colors.blue;
			case 'Menuiserie':
				return this.colors.yellow;
			default:
				return this.colors.orange;
		}
	}

	// Ancienne méthode pour affecter une couleur à un employé modulo son id
	// getColorFromId(id: number) {
	// 	//const colorNumber = id % 5;
	// 	//return this.colorsPastelBase[colorNumber];

	// 	const colorNumber = id % this.nbrColorsPastel;
	// 	return this.colorsPastel[colorNumber];
	// }

	getColorFromEmployee(employee: Employee) {
		//const colorNumber = id % 5;
		//return this.colorsPastelBase[colorNumber];

		// Check if employee does not exist => unassigned
		const employeeId = employee?.id;
		if (!employeeId) {
			return this.colors.white;
		}

		const employeeIndexColor = employee?.colorIndex;

		// If indexColor not defined, return color for external employee
		if (!employeeIndexColor || employeeIndexColor < 0) {
			return this.colorExternalEmployee;
		}

		const colorNumber = employeeIndexColor % this.nbrColorsPastel;
		return this.colorsPastel[colorNumber];
	}

	loadPublicHolidays(): CalendarEvent[] {
		let resEvents: CalendarEvent[] = [];

		for (const dateKey in this.publicHolidays) {
			const event: CalendarEvent = {
				start: new Date(dateKey),
				title: this.sanitizer.sanitize(
					SecurityContext.HTML,
					this.publicHolidays[dateKey]
				),
				allDay: true,
				meta: {
					type: 'holiday',
					holiday: dateKey + ' : ' + this.publicHolidays[dateKey]
				}
			};
			resEvents.push(event);
		}

		return resEvents;
	}

	getTitleOfMission(mission: Mission, employee?: Employee): string {
		const titleEvent = this.buildTitleEvent(mission, employee);
		return mission.state === State.CANCELED
			? `<del>${titleEvent}</del>`
			: titleEvent;
	}

	private isReunion(mission: Mission): boolean {
		return mission.missionRequest.missionType.numberOfEmployee < 0;
	}

	private getBeneficiaireName(mission: Mission): string | null {
		const beneficiaire = mission.missionRequest?.beneficiaire;
		return beneficiaire
			? `${beneficiaire.firstName} ${beneficiaire.lastName}`
			: null;
	}

	private getInterventionInfo(
		mission: Mission,
		beneficiaireName: string | null
	): string {
		if (!beneficiaireName) return ''; // Retourne une chaîne vide si aucun bénéficiaire
		if (!this.isMissionPlacesActive || !mission.missionRequest?.missionPlace) {
			return `Domicile du ${this.labelBeneficiaireCapitalized}`;
		}
		return this.titleCasePipe.transform(
			mission.missionRequest.missionPlace.name
		);
	}

	private getAddressInfo(
		mission: Mission,
		beneficiaireName: string | null
	): string {
		if (!beneficiaireName) return ''; // Retourne une chaîne vide si aucun bénéficiaire
		if (!this.isMissionPlacesActive || !mission.missionRequest?.missionPlace) {
			return this.titleCasePipe.transform(
				mission.missionRequest?.beneficiaire?.address.label
			);
		}
		return this.titleCasePipe.transform(
			mission.missionRequest.missionPlace.address.label
		);
	}

	private getEmployeeInfo(
		isIntervenantPresent: boolean,
		employee: Employee | undefined
	): string | null {
		return isIntervenantPresent
			? `${employee.firstName} ${employee.lastName}`
			: null;
	}

	private buildTitleEvent(mission: Mission, employee?: Employee): string {
		const beneficiaireName = this.getBeneficiaireName(mission);
		const lieuIntervention = this.getInterventionInfo(
			mission,
			beneficiaireName
		);
		const adresseIntervention = this.getAddressInfo(mission, beneficiaireName);
		const isIntervenantPresent = Boolean(employee || mission.assignedEmployee);
		const employeeName = this.getEmployeeInfo(isIntervenantPresent, employee);

		let titleEvent = '<p class="lh-1">';
		if (this.isReunion(mission)) {
			titleEvent += this.formatReunionInfo(mission);
		} else {
			titleEvent += this.formatMissionInfo(
				mission,
				beneficiaireName,
				lieuIntervention,
				adresseIntervention,
				isIntervenantPresent,
				employeeName
			);
		}
		titleEvent += '</p>';
		return titleEvent;
	}

	private formatReunionInfo(mission: Mission): string {
		const hostName = mission.planner
			? `${mission.planner.firstName} ${mission.planner.lastName}`
			: '';
		return `<br><span class="fw-bold fs-6">${MISSION_TYPE_REUNION_TYPE}</span><br><span class="fw-bold">${
			this.labelHostCapitalized + hostName
		}</span>`;
	}

	private isGroupedMissionParent(mission: Mission) {
		return (
			mission.missionRequest.nombreIntervenant > 1 &&
			mission.missionParent === null
		);
	}

	private hasGroupedMissionParent(mission: Mission) {
		return (
			!this.isGroupedMissionParent(mission) &&
			mission.missionParent &&
			mission.missionParent.missionRequest.nombreIntervenant > 1
		);
	}

	private formatEmployeeInformation(
		isIntervenantPresent: boolean,
		employeeName: string | null,
		mission: Mission
	): string {
		const isGroupedMissionParent = this.isGroupedMissionParent(mission);
		const hasGroupedMissionParent = this.hasGroupedMissionParent(mission);
		if (
			isIntervenantPresent &&
			!isGroupedMissionParent &&
			!hasGroupedMissionParent
		) {
			return `<span class="fw-bold"> ${this.labelIntervenantCapitalized} </span> : ${employeeName} <br>`;
		} else if (hasGroupedMissionParent) {
			return this.formatGroupedParentAttendees(mission, true);
		} else if (isGroupedMissionParent) {
			return this.formatGroupedParentAttendees(mission, false);
		}
		return '';
	}

	private formatGroupedParentAttendees(
		mission: Mission,
		useParent: boolean
	): string {
		const attendees = useParent
			? mission.missionParent.attendees
			: mission.attendees;
		const attendeesNames = attendees
			? attendees
					.map((attendee) => ` ${attendee.firstName} ${attendee.lastName}`)
					.join(' ')
			: 'Aucun';
		return `<span class="fw-bold"> ${this.labelIntervenantCapitalized}s </span> :<br> ${attendeesNames} <br>`;
	}

	private formatMissionInfo(
		mission: Mission,
		beneficiaireName: string | null,
		lieuIntervention: string,
		adresseIntervention: string,
		isIntervenantPresent: boolean,
		employeeName: string | null
	): string {
		const employeeInfoHTML = this.formatEmployeeInformation(
			isIntervenantPresent,
			employeeName,
			mission
		);

		const labelType = 'Type';
		const typeName = mission?.missionRequest?.missionType?.type;

		const labelSpec = 'Spécialité';
		const specName = mission?.missionRequest?.careSpecialty
			? mission?.missionRequest?.careSpecialty.name
			: '';

		const isPrescription =
			this.isSAE &&
			mission.missionRequest.missionType.type === MISSION_TYPE_PRESCRIPTION;

		// Construire le HTML conditionnellement en fonction des données disponibles
		return `<br>${
			beneficiaireName
				? `<span class="fw-bold fs-6">${beneficiaireName}</span><br><br>`
				: isPrescription
				? `<span class="fw-bold fs-6">${MISSION_TYPE_PRESCRIPTION}</span><br><br>`
				: ''
		}
			${lieuIntervention ? `${lieuIntervention}<br>` : ''}
			${adresseIntervention ? `${adresseIntervention}<br><br>` : ''}
			${employeeInfoHTML}<br>
			${
				this.isSESSAD || this.isSAE
					? `<span class="fw-bold">${labelType}</span> : 
			${typeName} <br>`
					: specName !== ''
					? `<span class="fw-bold">${labelSpec}</span> : 
			${specName} <br>`
					: ''
			}<span class="fw-bold">Date</span> : ${new Date(
			mission?.missionRequest.date
		).toLocaleDateString('fr-FR')}
			 ${this.dateUtils.timeConvert(
					mission.realStart
				)} - ${this.dateUtils.timeConvert(mission?.realEnd)}
			`;
	}

	convertMissionToCalendarEvent(mission: Mission): CalendarEvent[] {
		const eventList = [];

		const title = mission.assignedEmployee
			? this.getTitleOfMission(mission, mission.assignedEmployee)
			: this.getTitleOfMission(mission);

		const isMissionCancelled = State[mission.state].includes(State.CANCELED);
		// si c'est la mission parent de missions groupées, l'afficher différement
		const isGroupedMissionParent =
			mission.missionRequest.nombreIntervenant > 1 &&
			mission.missionParent === null;

		const startTimeMin =
			mission.missionRequest.assignedToPlanning || mission.assignedEmployee
				? mission.plannedStart
				: mission.missionRequest.readyTime;
		const endTimeMin =
			mission.missionRequest.assignedToPlanning || mission.assignedEmployee
				? mission.plannedEnd
				: mission.missionRequest.dueTime;

		const eventColor = isMissionCancelled
			? this.colors.red
			: isGroupedMissionParent
			? null
			: mission.missionRequest.assignedToPlanning || mission.assignedEmployee
			? this.getColorFromEmployee(mission.assignedEmployee)
			: this.colors.white;

		// Si pas réunion ou assigné => afficher temps de trajet
		const isTravelTimeExisting =
			mission.realTravelEnd - mission.realTravelStart > 0;
		if (
			isTravelTimeExisting &&
			(!mission.missionRequest.missionType.autoAssignment ||
				mission.missionRequest.assignedToPlanning)
		) {
			const inflatedTravelStart =
				mission.realStart - (mission.realTravelEnd - mission.realTravelStart);

			const eventTravel: CalendarEvent = {
				start: new Date(
					new Date(mission.missionRequest.date).getTime() +
						inflatedTravelStart * 60 * 1000
				),
				end: new Date(
					new Date(mission.missionRequest.date).getTime() +
						mission.realStart * 60 * 1000
				),
				title: this.sanitizer.sanitize(
					SecurityContext.HTML,
					'Temps de trajet de ' +
						(mission.realTravelEnd - mission.realTravelStart) +
						'min'
				),
				color: null,

				resizable: {
					beforeStart: false,
					afterEnd: false
				},
				draggable: false,
				meta: {
					type: 'travel',
					mission,
					employee: mission.assignedEmployee
				}
			};

			eventList.push(eventTravel);
		}

		const eventMission: CalendarEvent = {
			start: new Date(
				new Date(mission.missionRequest.date).getTime() +
					startTimeMin * 60 * 1000
			),
			end: new Date(
				new Date(mission.missionRequest.date).getTime() + endTimeMin * 60 * 1000
			),
			title: this.sanitizer.sanitize(SecurityContext.HTML, title),
			color: eventColor,

			resizable: {
				beforeStart: false,
				afterEnd: false
			},
			draggable: false,
			meta: {
				type: 'mission',
				mission,
				employee: mission.assignedEmployee
			}
		};

		//Cas spécial pour les réunion
		if (mission.missionRequest.missionType.numberOfEmployee === -1) {
			eventMission.color = this.colors.dark;
		}
		eventList.push(eventMission);

		return eventList;
	}

	convertRouteToCalendarEvent(route: Route, employee: Employee): CalendarEvent {
		if (route.missions.length > 0) {
			// on trie les missions de la route par heure de début
			route.missions.sort((a, b) => a.realStart - b.realStart);
			const startTimeMin = route.depotStartTime;
			const endTimeMin = route.depotEndTime;

			const date = new Date(route.missions[0]?.missionRequest.date);
			const startDate = new Date(date.getTime() + startTimeMin * 60 * 1000);
			const endDate = new Date(date.getTime() + endTimeMin * 60 * 1000);

			const empName = employee.firstName + ' ' + employee.lastName;

			let nombreMissions = route.missions.length;

			let missionsString = '';
			let isBreakLaunchCreated = false;
			route.missions.forEach((mission, index) => {
				if (
					mission.missionRequest?.missionType?.type ===
						MISSION_TYPE_REUNION_TYPE &&
					mission.missionParent === null
				) {
					// pas de reunion missionParent dans les routes : on la retire du compte
					nombreMissions--;
					return;
				}
				if (
					nombreMissions > 1 && // il faut + d'1 mission pour avoir une pause dej
					index > 0 && // pause dej ne peut pas être en 1ere position
					!isBreakLaunchCreated && // pause dej unique
					mission.realStart > 720 && // après 12h00
					mission.realStart < 900 && // avant 15h00
					index < nombreMissions // si il reste des missions l'après-midi
				) {
					isBreakLaunchCreated = true;
					const canBreakLaunchStartAt12 =
						mission.realStart > 720 + this.breakLaunchDuration &&
						route.missions[index - 1]?.realEnd < 720;
					const canBreakLaunchStartAfterPreviousMission =
						mission.realStart - route.missions[index - 1]?.realEnd >
						this.breakLaunchDuration;

					const startBreakLaunch = canBreakLaunchStartAt12
						? 720
						: canBreakLaunchStartAfterPreviousMission
						? route.missions[index - 1]?.realEnd
						: mission.realStart - this.breakLaunchDuration;

					missionsString +=
						'<span class="theme-main-color"> - Pause déjeuner : ' +
						// this.dateUtils.timeConvert(startBreakLaunch) +
						// ' - ' +
						// this.dateUtils.timeConvert(
						// 	startBreakLaunch + this.breakLaunchDuration
						// ) +
						this.breakLaunchDuration +
						' min' +
						'</span><br>';
				}
				if (this.state[mission.state].includes(this.state.CANCELED)) {
					missionsString += '<del>';
				}
				if (
					mission.missionRequest?.missionType.type === MISSION_TYPE_REUNION_TYPE
				) {
					missionsString +=
						'- ' +
						MISSION_TYPE_REUNION_TYPE +
						' : ' +
						this.dateUtils.timeConvert(mission.realStart) +
						' - ' +
						this.dateUtils.timeConvert(mission.realEnd);
				} else {
					missionsString +=
						'- ' +
						mission.missionRequest.beneficiaire?.firstName +
						' ' +
						mission.missionRequest.beneficiaire?.lastName +
						' : ' +
						this.dateUtils.timeConvert(mission.realStart) +
						' - ' +
						this.dateUtils.timeConvert(mission.realEnd);
				}

				if (this.state[mission.state].includes(this.state.CANCELED)) {
					missionsString += '</del>';
				}
				missionsString += '<br>';
			});

			const title = `<p class="lh-sm"> <br>
			<span class="fw-bold  fs-6">${empName}</span> <br>
			${this.dateUtils.timeConvert(startTimeMin)} - ${this.dateUtils.timeConvert(
				endTimeMin
			)} <br>
			${
				this.facade.dataStorageService.isVehiculeActive() && route.vehicle
					? 'Véhicule : ' +
					  route.vehicle.name +
					  ' - ' +
					  route.vehicle.model +
					  '<br>'
					: ''
			}
			${
				nombreMissions +
				' ' +
				(route.missions.length > 1
					? this.labelInterventionCapitalized + 's'
					: this.labelInterventionCapitalized)
			} : <br>
			${missionsString}
			</p>`;

			const event: CalendarEvent = {
				start: startDate,
				end: endDate,
				title: this.sanitizer.sanitize(SecurityContext.HTML, title),
				color: this.getColorFromEmployee(employee),
				resizable: {
					beforeStart: false,
					afterEnd: false
				},
				draggable: false,
				meta: {
					type: 'route',
					route,
					employee
				}
			};
			return event;
		}
	}

	convertAbsencesItfcToCalendarEvent(employee: Employee): CalendarEvent[] {
		const resEvents: CalendarEvent[] = [];
		employee.vacations.forEach((vacation) =>
			resEvents.push(this.convertAbsenceToCalendarEvent(vacation, employee))
		);
		return resEvents;
	}

	convertAbsenceToCalendarEvent(
		vacation: Vacation,
		employee: Employee
	): CalendarEvent {
		const titleOfEvent = `<p class="lh-1"></p><span class="fw-bold fs-6">${this.titleCasePipe.transform(
			`${employee.firstName} ${employee.lastName}`
		)}</span><br>
         ${vacation.vacationType.type} </p>`;
		const event: CalendarEvent = {
			start: new Date(
				new Date(vacation.startDate).getTime() + vacation.startTime * 60 * 1000
			),
			end: new Date(
				new Date(vacation.endDate).getTime() + vacation.endTime * 60 * 1000
			),
			title: this.sanitizer.sanitize(SecurityContext.HTML, titleOfEvent),
			color: this.colors.lightgrey,
			// actions: this.actions,
			resizable: {
				beforeStart: false,
				afterEnd: false
			},
			draggable: false,
			meta: {
				type: 'vacation',
				vacation,
				employee
			}
		};
		return event;
	}
}
